import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { useCommunityDetails } from 'atoms/community-details';
import { CommunityUserGroup } from '@cocoplatform/coco-rtc-shared';
import { UserList } from 'components/community-members-explorer/CommunityMembersExplorer';
import ControlLabel from 'components/control-label/ControlLabel';
import ErrorList from 'components/forms/ErrorList';
import ModalDialog from 'components/modal-dialog/ModalDialog';
import SearchInputField from 'components/search-input-field/SearchInputField';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import React, { useState, useMemo, FormEvent } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import FlexRow from 'components/flex/FlexRow';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Stack, Typography } from '@mui/material';

function GetModalHeading(p: { group?: CommunityUserGroup }) {
  const { _ } = useLingui();
  const heading = p.group ? _(msg`Update Group`) : _(msg`New Group`);

  return (
    <Stack>
      <Typography mx={5} variant='h2'>
        {heading}
      </Typography>
      {!p?.group?.googleClassroom?.id && (
        <Typography variant='caption'>
          <Trans>
            You can organize members into groups, such as by grades, etc.
          </Trans>
        </Typography>
      )}
    </Stack>
  );
}

export default function GroupFormModal(p: {
  communityId: string;
  group?: CommunityUserGroup;
  onClose?: () => void;
}) {
  const { _ } = useLingui();
  const [name, setName] = useState(p.group?.name ?? '');
  const [isSearchActive, setSearchActive] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const { createGroup, updateGroup, community } = useCommunityDetails({
    communityId: p.communityId,
    autoLoad: false,
  });
  const cMembers = community?.members ?? [];
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    p.group
      ? p.group.userIds
      : compact(cMembers.filter((m) => m.isSelf).map((m) => m.user?.id)),
  );
  const members = useMemo(() => {
    return cMembers.filter((m) => {
      return m.isApproved && m.user;
    });
  }, [cMembers]);

  return (
    <ModalDialog
      isCloseable
      title={<GetModalHeading group={p.group} />}
      onClose={p.onClose}
    >
      <form onSubmit={handleSubmit}>
        <ErrorList errors={errors} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ControlLabel>
              <Trans>Name of Group</Trans>
            </ControlLabel>
            <TextField
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              fullWidth
              multiline={false}
              size='small'
            />
          </Grid>
          {!p?.group?.googleClassroom?.id && (
            <>
              <Grid item xs={12}>
                <FlexRow>
                  <ControlLabel style={{ flexGrow: 1 }}>
                    <Trans>Select Users</Trans>
                  </ControlLabel>
                  <IconButton onClick={() => setSearchActive((s) => !s)}>
                    <SearchIcon />
                  </IconButton>
                </FlexRow>
                {isSearchActive && (
                  <SearchInputField
                    value={userSearchQuery}
                    fullWidth
                    onChange={(e) => {
                      setUserSearchQuery(e.target.value);
                    }}
                    onClear={() => {
                      setSearchActive(false);
                    }}
                  />
                )}
                <div
                  style={{
                    height: '200px',
                    overflow: 'auto',
                    marginTop: '10px',
                  }}
                >
                  <UserList
                    communityId={p.communityId}
                    members={members}
                    selectedUserIds={selectedUserIds}
                    query={userSearchQuery}
                    showControls={false}
                    onSelect={(userId, isSelected) => {
                      if (!isSelected) {
                        setSelectedUserIds((ids) =>
                          ids.filter((id) => userId !== id),
                        );
                        return;
                      }
                      setSelectedUserIds((ids) => ids.concat(userId));
                    }}
                  />
                </div>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <LoadingButton
              loading={isSubmitting}
              size='large'
              fullWidth
              variant='contained'
              color='primary'
              type='submit'
            >
              {p.group ? <Trans>Update</Trans> : <Trans>Add</Trans>}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </ModalDialog>
  );

  async function handleSubmit(e: FormEvent) {
    e.stopPropagation();
    e.preventDefault();

    const errors: string[] = [];
    if (isEmpty(name)) errors.push(_(msg`Name must not be empty`));
    if (isEmpty(selectedUserIds) && !p.group?.googleClassroom?.id)
      errors.push(_(msg`At least one user must be added`));
    setErrors(errors);
    if (!isEmpty(errors)) return;
    setSubmitting(true);
    try {
      if (p.group) {
        await updateGroup(p.group.id, name, selectedUserIds);
      } else {
        await createGroup(name, selectedUserIds);
      }
    } finally {
      setSubmitting(false);
    }
    p.onClose?.();
  }
}
